import {NoticeMessageErrorDetails} from '#/atoms/notice-message-error-details';
import {UseQueryResult} from '@tanstack/react-query';
import {FunctionComponent} from 'react';
import {useTranslation} from 'react-i18next';

export const TokenTransferStatus: FunctionComponent<{
  title: string;
  queryEnabled: boolean;
  query: UseQueryResult;
  idleMessage?: string;
  bypass?: boolean;
}> = ({title, queryEnabled, query, bypass, idleMessage = 'idle'}) => {
  const {t} = useTranslation('translation');

  const {status, bgColor, text} = (() => {
    if (bypass) {
      return {
        status: 'success',
        bgColor: 'bg-upgreen',
        text: t('components.tokenTransferStatus.bypass'),
      };
    }

    if (!queryEnabled) {
      return {
        status: 'idle',
        bgColor: 'bg-gray-400',
        text: idleMessage ?? t('components.tokenTransferStatus.disabled'),
      };
    }

    if (!!query.error) {
      return {
        status: 'error',
        bgColor: 'bg-downred',
        text: '',
      };
    }

    if (query.status === 'pending') {
      return {
        status: 'loading',
        bgColor: 'bg-yellow-500',
        text: t('components.tokenTransferStatus.loading'),
      };
    }

    return {
      status: 'success',
      bgColor: 'bg-upgreen',
      text: t('components.tokenTransferStatus.success'),
    };
  })();

  return (
    <div className='w-full self-center space-y-2 text-sm'>
      <div className='flex justify-between items-center gap-4'>
        <div className=''>
          <div className='flex items-center gap-2 whitespace-nowrap overflow-hidden flex-1'>
            <div className={`w-3 h-3 rounded-full ${bgColor} justify-self-start`} />
            <span className='font-bold capitalize justify-self-start whitespace-nowrap'>{title}</span>
          </div>

          <span
            className={`grow text-gray-400 italic justify-self-start ${status === 'loading' ? 'animate-pulse' : ''}`}
          >
            {text}
          </span>
        </div>

        {status === 'error' && (
          <button
            className='hover:opacity-70 align-middle py-1 px-2 overflow-hidden text-white rounded bg-downred relative'
            onClick={() => {
              query.refetch();
            }}
          >
            {t('actions.retry')}
          </button>
        )}
      </div>
      {!!query.error && <NoticeMessageErrorDetails error={query.error as Error} />}
    </div>
  );
};
